import { localPrice } from "@/api/dtos";
import { t } from "i18next";
import { ComponentProps, type ReactNode } from "react";
import { ComputeFieldComponent } from "../computeFields";
import {
  Artwork,
  ArtworkHistory,
  ArtworkMonetaryTransactionEstimate,
} from "../types";
import {
  decodeHtmlEntities,
  getOeuvresDescriptionSecondaryTitleLines,
  resolveOeuvresDescriptionDisplayTitle,
} from "../utils";
import { DimensionsFormatting, isValidDimField } from "./shared/dimensions";
import { InscriptionDosCF } from "./shared/inscription-dos";
import { LocalisationCF } from "./shared/localisation";
import { RelatedAuthorsListCF } from "./shared/related-authors";
import { SignatureCF } from "./shared/signature";
import { SupportTechniqueCF } from "./shared/technique";
import {
  CreditCF,
  DateCF,
  MainTitleCF,
  TitleCF,
  TitlePhotoMasterCF,
} from "./shared/title";
import { SeparatedList } from "./shared/utils";
import { ArtworkWeightCF } from "./shared/weight";

/**
 * Vérifie si un champ JSON ou string est rempli (non vide)
 */
const isFieldFilled = (field: any): boolean => {
  if (!field) return false;
  if (typeof field === "string") {
    return field.trim().length > 0;
  }
  if (typeof field === "object") {
    // Pour LocaleDict ou objets JSON, vérifier qu'au moins une valeur existe et n'est pas vide
    return Object.values(field).some(
      (value) => typeof value === "string" && value.trim().length > 0,
    );
  }
  return false;
};

export const ArtworkDimensionsCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionDimensionsOeuvre"
    | "oeuvresDescriptionDimensionsOeuvreRaw"
    | "oeuvresDescriptionDimensionsOeuvreInconnues"
  >
> = ({ input, ...props }) => {
  return (
    <DimensionsFormatting
      input={{
        ...input.oeuvresDescriptionDimensionsOeuvre,
        rawDimensions: input.oeuvresDescriptionDimensionsOeuvreRaw,
        unknownDimensions: input.oeuvresDescriptionDimensionsOeuvreInconnues,
      }}
      {...props}
    />
  );
};

export const ArtworkDimensionsHorsMargesCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionDimensionsHorsMarges"
    | "oeuvresDescriptionDimensionsHorsMargesInconnues"
  >
> = ({ input, ...props }) => {
  return (
    <DimensionsFormatting
      {...props}
      input={{
        ...input.oeuvresDescriptionDimensionsHorsMarges,
        unknownDimensions:
          input.oeuvresDescriptionDimensionsHorsMargesInconnues,
      }}
    />
  );
};

export const ArtworkDimensionsCadreCF: ComputeFieldComponent<
  Pick<Artwork, "oeuvresRegieDimensionsCadre">
> = ({ input, ...props }) => (
  <DimensionsFormatting
    input={{ ...input.oeuvresRegieDimensionsCadre }}
    {...props}
  />
);

export const ArtworkRelatedAuthorsCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "relatedAuthor"
    | "oeuvresDescriptionNomArtisteAnonyme"
    | "oeuvresDescriptionNomArtisteDApres"
    | "oeuvresDescriptionNomArtisteAttribueA"
    | "oeuvresDescriptionNomArtisteDansLeGoutDe"
    | "oeuvresDescriptionNomArtisteEntourageDe"
    | "oeuvresDescriptionNomArtisteAtelierDe"
  >
> = ({ input, ...props }) => (
  <RelatedAuthorsListCF
    input={{
      relatedAuthors: input.relatedAuthor,
      attributedTo: input.oeuvresDescriptionNomArtisteAttribueA,
      from: input.oeuvresDescriptionNomArtisteDApres,
      tasteOf: input.oeuvresDescriptionNomArtisteDansLeGoutDe,
      entourageOf: input.oeuvresDescriptionNomArtisteEntourageDe,
      anonymous: input.oeuvresDescriptionNomArtisteAnonyme,
      studioOf: input.oeuvresDescriptionNomArtisteAtelierDe,
    }}
    {...props}
  />
);

export const ArtworkSupportTechniqueCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTechnique"
    | "oeuvresDescriptionSupport"
    | "oeuvresDescriptionOeuvreDoubleFace"
  > & {
    preposition?: string;
    doubleFace?: string;
  }
> = ({ input, ...props }) => (
  <SupportTechniqueCF
    input={{
      ...input,
      technique: input.oeuvresDescriptionTechnique,
      support: input.oeuvresDescriptionSupport,
      isDoubleSided: input.oeuvresDescriptionOeuvreDoubleFace,
    }}
    {...props}
  />
);

export const ArtworkSignatureCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionSignatureLocalisation"
    | "oeuvresDescriptionSignatureTranscription"
  >
> = ({ input, ...props }) => {
  const transcriptionValue = input.oeuvresDescriptionSignatureTranscription;
  const transcription =
    typeof transcriptionValue === "string"
      ? transcriptionValue
      : transcriptionValue &&
          typeof transcriptionValue === "object" &&
          props.language
        ? ((transcriptionValue as Record<string, unknown>)[props.language] ??
          Object.values(transcriptionValue as Record<string, unknown>).find(
            (value) => typeof value === "string" && value.trim().length > 0,
          ) ??
          "")
        : "";

  return (
    <SignatureCF
      input={{
        location: input.oeuvresDescriptionSignatureLocalisation,
        transcription: typeof transcription === "string" ? transcription : "",
      }}
      {...props}
    />
  );
};

export const ArtworkInscriptionDosCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionInscriptionsDosMultilingual"
    | "oeuvresDescriptionInscriptionsDosPublished"
  >
> = ({ input, inline = false, ...props }) => (
  <InscriptionDosCF
    input={{
      published: input.oeuvresDescriptionInscriptionsDosPublished,
      inscription: input.oeuvresDescriptionInscriptionsDosMultilingual,
    }}
    inline={inline}
    {...props}
  />
);

export const ArtworkLocalisationCF: ComputeFieldComponent<
  Pick<Artwork, "oeuvresLocalisationHistoriqueCollectionCatalogue">
> = ({ input, ...props }) => (
  <LocalisationCF
    input={{
      localisation: input.oeuvresLocalisationHistoriqueCollectionCatalogue,
    }}
    {...props}
  />
);

export const ArtworkMainTitleCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTitreInconnu"
    | "oeuvresDescriptionTitreSansTitre"
    | "oeuvresValidationTitleNotConfirmed"
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreationIncertaine"
    | "oeuvresDescriptionDateDeCreation"
  >
> = ({ input, ...props }) => (
  <MainTitleCF
    input={{
      title: decodeHtmlEntities(input.oeuvresDescriptionTitre ?? ""),
      titleState: {
        notConfirmed: input.oeuvresValidationTitleNotConfirmed,
        none: input.oeuvresDescriptionTitreSansTitre,
        unknown: input.oeuvresDescriptionTitreInconnu,
      },
    }}
    {...props}
  />
);

export const ArtworkDateCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreationIncertaine"
    | "oeuvresDescriptionDateDeCreation"
  >
> = ({ input, ...props }) => (
  <DateCF
    input={{
      date: input.oeuvresDescriptionDateDeCreation,
      unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      uncertainDate: input.oeuvresDescriptionDateDeCreationIncertaine,
    }}
    {...props}
  />
);

const CURRENCY_SIGNS: Record<string, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  CHF: "CHF",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
  HKD: "HK$",
  CNY: "C¥",
  DKK: "kr",
  SEK: "kr",
  NOK: "kr",
  PLN: "zł",
  CZK: "Kč",
};

function getCurrencySign(currency: string): string {
  return CURRENCY_SIGNS[currency] ?? currency;
}

/** Estimation column from Artwork.MonetaryTransaction (category estimate), one line per transaction. */
export const ArtworkEstimationFromMonetaryTransactionsCF: ComputeFieldComponent<
  Pick<Artwork, "MonetaryTransaction"> & {
    MonetaryTransaction?: ArtworkMonetaryTransactionEstimate[];
  }
> = ({ input, language }) => {
  const transactions = input.MonetaryTransaction ?? [];
  const latest = transactions.length === 0 ? null : transactions[0];
  if (!latest) {
    return <></>; // no estimation
  }
  const amountStr = localPrice(language, latest.amount);
  const sign = getCurrencySign(latest.currency);
  const line = `${latest.title}, ${amountStr} ${sign}, ${latest.date}`;
  return <>{line}</>;
};

export const ArtworkTitleCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTitreMultilingual"
    | "oeuvresDescriptionTitreMultilingualPublished"
    | "oeuvresDescriptionTitreSansTitre"
    | "oeuvresDescriptionTitreInconnu"
    | "oeuvresValidationTitleNotConfirmed"
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreationIncertaine"
    | "oeuvresDescriptionDateDeCreation"
    | "oeuvresDescriptionSousTitre"
    | "oeuvresDescriptionSousTitrePublished"
    | "oeuvresDescriptionAutreTitre"
    | "oeuvresDescriptionAutreTitrePublished"
  > & {
    showDate?: boolean;
  }
> = ({ input, language, ...props }) => {
  const resolvedTitle = resolveOeuvresDescriptionDisplayTitle(
    input,
    language,
    props.defaultLanguage,
  );
  const translate =
    props.translation ?? ((key: string) => t(key, { lng: language }));
  const secondaryTitleLines = getOeuvresDescriptionSecondaryTitleLines(
    input,
    language,
    props.defaultLanguage,
    translate,
  );
  const publishedSubtitle =
    input.oeuvresDescriptionSousTitrePublished === true
      ? input.oeuvresDescriptionSousTitre
      : undefined;
  const publishedOtherTitle =
    input.oeuvresDescriptionAutreTitrePublished === true
      ? input.oeuvresDescriptionAutreTitre
      : undefined;
  return (
    <TitleCF
      language={language}
      input={{
        title: decodeHtmlEntities(resolvedTitle),
        titleState: {
          notConfirmed: input.oeuvresValidationTitleNotConfirmed,
          none: input.oeuvresDescriptionTitreSansTitre,
          unknown: input.oeuvresDescriptionTitreInconnu,
        },
        subtitle: publishedSubtitle
          ? decodeHtmlEntities(publishedSubtitle)
          : publishedSubtitle,
        otherTitle: publishedOtherTitle
          ? decodeHtmlEntities(publishedOtherTitle)
          : publishedOtherTitle,
        secondaryTitleLines: secondaryTitleLines.map((line) =>
          decodeHtmlEntities(line),
        ),
        date: input.oeuvresDescriptionDateDeCreation
          ? decodeHtmlEntities(input.oeuvresDescriptionDateDeCreation)
          : input.oeuvresDescriptionDateDeCreation,
        unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      uncertainDate: input.oeuvresDescriptionDateDeCreationIncertaine,
        showDate: input.showDate,
      }}
      {...props}
    />
  );
};

export const UnstyledArtworkTitleCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkTitleCF>["input"]
> = ({ input, language, ...props }) => {
  return (
    <MainTitleCF
      language={language}
      {...props}
      input={{
        styled: false,
        title: decodeHtmlEntities(
          resolveOeuvresDescriptionDisplayTitle(
            input,
            language,
            props.defaultLanguage,
          ),
        ),
        titleState: {
          notConfirmed: input.oeuvresValidationTitleNotConfirmed,
          none: input.oeuvresDescriptionTitreSansTitre,
          unknown: input.oeuvresDescriptionTitreInconnu,
        },
      }}
    />
  );
};

export const ArtworkSummaryCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkRelatedAuthorsCF>["input"] &
    ComponentProps<typeof ArtworkTitleCF>["input"] &
    ComponentProps<typeof ArtworkDimensionsCF>["input"] &
    ComponentProps<typeof ArtworkSupportTechniqueCF>["input"] &
    ComponentProps<typeof ArtworkSignatureCF>["input"] &
    ComponentProps<typeof ArtworkInscriptionDosCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"]
> = ({ input, lineBreak = true, ...props }) => {
  return (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={[
          <ArtworkRelatedAuthorsCF input={input} {...props} />,
          <ArtworkTitleCF input={input} {...props} />,
          <ArtworkDimensionsCF input={input} {...props} />,
          <ArtworkSupportTechniqueCF input={input} {...props} />,
          <ArtworkSignatureCF input={input} {...props} />,
          <ArtworkInscriptionDosCF input={input} {...props} />,
          <ArtworkLocalisationCF input={input} {...props} />,
        ]}
      />
    </>
  );
};

export const RelatedEnsembleDescriptionCF: ComputeFieldComponent<
  Pick<Artwork, "relatedEnsemble">
> = ({ input, lineBreak = false }) => {
  const ensembleNames = input.relatedEnsemble
    .map((item) => item.ensemblesDescriptionNomEnsemble)
    .join(", ");

  return (
    <>
      {ensembleNames}
      {lineBreak && <br />}
    </>
  );
};

export const ArtworkDescriptionEnsembleFixeCF: ComputeFieldComponent<
  Pick<Artwork, "relatedEnsemble">
> = ({ input, ...props }) => {
  const ensembleList = input.relatedEnsemble.filter(
    (i) => i.ensemblesDescriptionType === "fixe",
  );

  return (
    <RelatedEnsembleDescriptionCF
      input={{ relatedEnsemble: ensembleList }}
      {...props}
    />
  );
};

export const ArtworkDescriptionEnsembleTemporaireCf: ComputeFieldComponent<
  Pick<Artwork, "relatedEnsemble">
> = ({ input, ...props }) => {
  const ensembleList = input.relatedEnsemble.filter(
    (i) => i.ensemblesDescriptionType === "temporaire",
  );

  return (
    <RelatedEnsembleDescriptionCF
      input={{ relatedEnsemble: ensembleList }}
      {...props}
    />
  );
};

export const ArtworkHistoryDescriptionCF: ComputeFieldComponent<
  Pick<
    ArtworkHistory,
    | "oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere"
    | "oeuvresLocalisationHistoriqueHistoriqueNom"
    | "oeuvresLocalisationHistoriqueHistoriqueVille"
  >
> = ({ input }) => {
  const location =
    input.oeuvresLocalisationHistoriqueHistoriqueCollectionParticuliere
      ? t("computeField.collectionParticuliere")
      : input.oeuvresLocalisationHistoriqueHistoriqueNom;

  const city = input.oeuvresLocalisationHistoriqueHistoriqueVille?.trim()
    ? `${location != undefined ? ", " : ""}${input.oeuvresLocalisationHistoriqueHistoriqueVille}`
    : "";

  return (
    <>
      {location ?? ""}
      {city}
    </>
  );
};

export const ArtworkLocalisationHistoriqueCF: ComputeFieldComponent<{
  artworkHistory?: ComponentProps<
    typeof ArtworkHistoryDescriptionCF
  >["input"][];
}> = ({ input, lineBreak = true, ...props }) => {
  if (!input.artworkHistory || input.artworkHistory.length === 0) {
    return <></>;
  }

  return (
    <SeparatedList
      {...props}
      input={input.artworkHistory.map((history, index) => (
        <ArtworkHistoryDescriptionCF key={index} input={history} {...props} />
      ))}
      lineBreak={lineBreak}
    />
  );
};

/*
 * PHOTOGRAPHY MASTER CF
 */
export const ArtworkTitlePhotoMasterCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTitreSansTitre"
    | "oeuvresDescriptionTitreInconnu"
    | "oeuvresValidationTitleNotConfirmed"
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionDateDeCreationInconnue"
    | "oeuvresDescriptionDateDeCreationIncertaine"
    | "oeuvresDescriptionDateDeCreation"
    | "oeuvresDescriptionLieuDeCreation"
  > & {
    showDate?: boolean;
  }
> = ({ input, ...props }) => (
  <TitlePhotoMasterCF
    input={{
      title: decodeHtmlEntities(input.oeuvresDescriptionTitre ?? ""),
      titleState: {
        notConfirmed: input.oeuvresValidationTitleNotConfirmed,
        none: input.oeuvresDescriptionTitreSansTitre,
        unknown: input.oeuvresDescriptionTitreInconnu,
      },

      location: input.oeuvresDescriptionLieuDeCreation,
      date: input.oeuvresDescriptionDateDeCreation,
      unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      uncertainDate: input.oeuvresDescriptionDateDeCreationIncertaine,
      showDate: input.showDate,
    }}
    {...props}
  />
);

export const ArtworkCreditOeuvreCF: ComputeFieldComponent<
  Pick<Artwork, "oeuvresCreditsCreditOeuvre">
> = ({ input, ...props }) => (
  <CreditCF input={{ credit: input.oeuvresCreditsCreditOeuvre }} {...props} />
);

export const ArtworkSummaryPhotographieMasterCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkRelatedAuthorsCF>["input"] &
    ComponentProps<typeof ArtworkTitlePhotoMasterCF>["input"] &
    ComponentProps<typeof ArtworkCreditOeuvreCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"] &
    ComponentProps<typeof ArtworkDimensionsCF>["input"] &
    ComponentProps<typeof ArtworkSupportTechniqueCF>["input"] &
    ComponentProps<typeof ArtworkSignatureCF>["input"] &
    ComponentProps<typeof ArtworkInscriptionDosCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"]
> = ({ input, lineBreak = true, ...props }) => {
  const dimensions = input.oeuvresDescriptionDimensionsOeuvre;

  const invalidDimensions =
    dimensions &&
    !isValidDimField(dimensions.cm_width) &&
    !isValidDimField(dimensions.cm_height) &&
    !isValidDimField(dimensions.cm_depth) &&
    !isValidDimField(dimensions.cm_diameter);

  const CFWithoutDimensions = (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={[
          <ArtworkRelatedAuthorsCF input={input} {...props} />,
          <ArtworkTitlePhotoMasterCF input={input} {...props} />,
          <ArtworkCreditOeuvreCF input={input} {...props} />,
        ]}
      />
    </>
  );

  if (
    (!dimensions || invalidDimensions) &&
    !input.oeuvresDescriptionDimensionsOeuvreRaw
  ) {
    return CFWithoutDimensions;
  }

  return (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={[
          <ArtworkRelatedAuthorsCF input={input} {...props} />,
          <ArtworkTitlePhotoMasterCF input={input} {...props} />,
          <ArtworkSupportTechniqueCF input={input} {...props} />,
          <ArtworkDimensionsCF input={input} {...props} />,
          <SeparatedList
            {...props}
            lineBreak={false}
            input={[
              <ArtworkSignatureCF input={input} {...props} />,
              <ArtworkInscriptionDosCF
                input={input}
                inline={
                  isFieldFilled(
                    input.oeuvresDescriptionSignatureTranscription,
                  ) ||
                  isFieldFilled(input.oeuvresDescriptionSignatureLocalisation)
                }
                {...props}
              />,
            ]}
          />,
          <ArtworkLocalisationCF input={input} {...props} />,
          <ArtworkCreditOeuvreCF input={input} {...props} />,
        ]}
      />
    </>
  );
};

export const ArtworkOverviewSummaryCF: ComputeFieldComponent<
  ComponentProps<typeof ArtworkDateCF>["input"] &
    ComponentProps<typeof ArtworkTitlePhotoMasterCF>["input"] &
    ComponentProps<typeof ArtworkSupportTechniqueCF>["input"] &
    ComponentProps<typeof ArtworkDimensionsCF>["input"] &
    ComponentProps<typeof ArtworkWeightCF>["input"] &
    ComponentProps<typeof ArtworkSignatureCF>["input"] &
    ComponentProps<typeof ArtworkInscriptionDosCF>["input"] &
    ComponentProps<typeof ArtworkLocalisationCF>["input"] &
    ComponentProps<typeof ArtworkCreditOeuvreCF>["input"] & {
      preposition?: string;
      doubleFace?: string;
      itemType?: "ARTWORK" | "PHOTO_MASTER" | "TIRAGE" | "SCULPTURE";
    },
  {
    isPhotoModuleEnabled: boolean;
    hasValidDimensions: boolean;
  }
> = ({
  input,
  lineBreak = true,
  isPhotoModuleEnabled,
  hasValidDimensions,
  ...props
}) => {
  const shouldShowSpecificFieldsInPhotoModule = input.itemType !== "TIRAGE";

  // Cas 1 : Pas de module photo
  if (!isPhotoModuleEnabled) {
    const overviewPieces = [
      <DateCF
        key="date"
        input={{
          date: input.oeuvresDescriptionDateDeCreation,
          unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      uncertainDate: input.oeuvresDescriptionDateDeCreationIncertaine,
        }}
        {...props}
      />,
      <ArtworkSupportTechniqueCF
        key="support-technique"
        input={input}
        {...props}
      />,
    ];
    const showOverviewDimensions =
      input.itemType !== "SCULPTURE" || hasValidDimensions;
    if (showOverviewDimensions) {
      overviewPieces.push(
        <ArtworkDimensionsCF key="dimensions" input={input} {...props} />,
      );
    }
    overviewPieces.push(
      <ArtworkWeightCF key="weight" input={input} {...props} />,
    );

    overviewPieces.push(
      <ArtworkSignatureCF key="signature" input={input} {...props} />,
      <ArtworkInscriptionDosCF
        key="inscription-dos"
        input={input}
        {...props}
      />,
      <ArtworkLocalisationCF key="localisation" input={input} {...props} />,
    );
    return (
      <>
        <SeparatedList
          {...props}
          lineBreak={lineBreak}
          input={overviewPieces}
        />
      </>
    );
  }

  // Cas 2 : Module photo sans dimensions
  if (!hasValidDimensions) {
    return (
      <>
        <SeparatedList
          {...props}
          lineBreak={lineBreak}
          input={[
            <SeparatedList
              {...props}
              lineBreak={false}
              input={[
                input.oeuvresDescriptionLieuDeCreation,
                <DateCF
                  input={{
                    date: input.oeuvresDescriptionDateDeCreation,
                    unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      uncertainDate: input.oeuvresDescriptionDateDeCreationIncertaine,
                  }}
                  {...props}
                />,
              ]}
            />,
            <ArtworkLocalisationCF input={input} {...props} />,
            <ArtworkCreditOeuvreCF input={input} {...props} />,
          ]}
        />
      </>
    );
  }

  // Cas 3 : Module photo avec dimensions
  const photoModuleElements: ReactNode[] = [
    <SeparatedList
      key="location-date"
      {...props}
      lineBreak={false}
      input={[
        input.oeuvresDescriptionLieuDeCreation,
        <DateCF
          key="date"
          input={{
            date: input.oeuvresDescriptionDateDeCreation,
            unknownDate: input.oeuvresDescriptionDateDeCreationInconnue,
      uncertainDate: input.oeuvresDescriptionDateDeCreationIncertaine,
          }}
          {...props}
        />,
      ]}
    />,
  ];

  if (shouldShowSpecificFieldsInPhotoModule) {
    photoModuleElements.push(
      <SupportTechniqueCF
        key="support-technique"
        input={{
          technique: input.oeuvresDescriptionTechnique,
          support: input.oeuvresDescriptionSupport,
          isDoubleSided: input.oeuvresDescriptionOeuvreDoubleFace,
          preposition: input.preposition,
          doubleFace: input.doubleFace,
        }}
        {...props}
      />,
    );
  }
  if (
    shouldShowSpecificFieldsInPhotoModule &&
    (input.itemType !== "SCULPTURE" || hasValidDimensions)
  ) {
    photoModuleElements.push(
      <ArtworkDimensionsCF key="dimensions" input={input} {...props} />,
    );
  }

  if (shouldShowSpecificFieldsInPhotoModule) {
    photoModuleElements.push(
      <ArtworkWeightCF key="weight" input={input} {...props} />,
    );
  }

  if (input.itemType !== "SCULPTURE") {
    photoModuleElements.push(
      <SeparatedList
        key="signature-inscription"
        {...props}
        lineBreak={false}
        input={[
          <ArtworkSignatureCF key="signature" input={input} {...props} />,
          <ArtworkInscriptionDosCF
            key="inscription-dos"
            input={input}
            inline={
              isFieldFilled(input.oeuvresDescriptionSignatureTranscription) ||
              isFieldFilled(input.oeuvresDescriptionSignatureLocalisation)
            }
            {...props}
          />,
        ]}
      />,
    );
  }
  photoModuleElements.push(
    <ArtworkLocalisationCF key="localisation" input={input} {...props} />,
    <ArtworkCreditOeuvreCF key="credit" input={input} {...props} />,
  );

  return (
    <>
      <SeparatedList
        {...props}
        lineBreak={lineBreak}
        input={photoModuleElements}
      />
    </>
  );
};

/**
 * Dimensions sans le texte « inconnues » : vide si aucune dimension valide.
 * Parité `ArtworkDimensionsMinifyCF` (front public).
 */
const ArtworkDimensionsMinifyCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionDimensionsOeuvre"
    | "oeuvresDescriptionDimensionsOeuvreInconnues"
    | "oeuvresDescriptionDimensionsOeuvreRaw"
  >
> = ({ input }) => {
  if (input.oeuvresDescriptionDimensionsOeuvreInconnues) return <></>;

  if (input.oeuvresDescriptionDimensionsOeuvreRaw) {
    return (
      <div className="text-xs font-semibold">
        {input.oeuvresDescriptionDimensionsOeuvreRaw}
      </div>
    );
  }

  const dimensions = input.oeuvresDescriptionDimensionsOeuvre;
  if (!dimensions) return <></>;

  const join = (parts: unknown[]) => parts.filter(isValidDimField).join(" x ");
  const diameter = (value: unknown) =>
    isValidDimField(value) ? `ø ${value}` : undefined;

  const cmDimensions = join([
    dimensions.cm_height,
    dimensions.cm_width,
    dimensions.cm_depth,
    diameter(dimensions.cm_diameter),
  ]);
  if (!cmDimensions) return <></>;

  const inDimensions = join([
    dimensions.in_height,
    dimensions.in_width,
    dimensions.in_depth,
    diameter(dimensions.in_diameter),
  ]);

  return (
    <div className="text-xs font-semibold">
      {cmDimensions} cm - {inDimensions} in.
    </div>
  );
};

/**
 * Résumé d'une carte de listing du catalogue : titre, date (ou lieu + date),
 * technique sur support, dimensions. Parité `ArtworkMinifySummaryCF` du front
 * public — à garder alignée avec lui.
 */
export const ArtworkMinifySummaryCF: ComputeFieldComponent<
  Pick<
    Artwork,
    | "oeuvresDescriptionTitre"
    | "oeuvresDescriptionTitreMultilingual"
    | "oeuvresDescriptionTitreMultilingualPublished"
    | "oeuvresDescriptionDateDeCreation"
    | "oeuvresDescriptionLieuDeCreation"
    | "oeuvresDescriptionTechnique"
    | "oeuvresDescriptionSupport"
    | "oeuvresDescriptionOeuvreDoubleFace"
    | "oeuvresDescriptionDimensionsOeuvre"
    | "oeuvresDescriptionDimensionsOeuvreInconnues"
    | "oeuvresDescriptionDimensionsOeuvreRaw"
  > & { itemType?: string }
> = ({ input, ...props }) => {
  const hideUnknownDimensions =
    input.itemType === "PHOTO_MASTER" ||
    input.itemType === "TIRAGE" ||
    input.itemType === "EXEMPLAIRE" ||
    input.itemType === "SCULPTURE";

  const lieuDate = [
    hideUnknownDimensions ? input.oeuvresDescriptionLieuDeCreation : undefined,
    input.oeuvresDescriptionDateDeCreation,
  ]
    .filter(Boolean)
    .join(", ");

  const title = resolveOeuvresDescriptionDisplayTitle(
    input,
    props.language,
    props.defaultLanguage,
  );

  return (
    <>
      <p className="font-semibold">{title}</p>
      {lieuDate && <p className="text-base">{lieuDate}</p>}
      <p className="text-xs">
        <ArtworkSupportTechniqueCF input={input} {...props} />
      </p>
      {hideUnknownDimensions ? (
        <ArtworkDimensionsMinifyCF input={input} {...props} />
      ) : (
        <div className="text-xs font-semibold">
          <ArtworkDimensionsCF input={input} {...props} />
        </div>
      )}
    </>
  );
};
